import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { isAdmin, getToken } from "./auth/token";
import BACKEND_URL from "./config";

import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import AdminInterviewsTable from "./components/AdminInterviewsTable";



// Reuse the same auth header helper as other admin pages
function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Fallback for browsers/contexts where navigator.clipboard is unavailable.
function copyViaTextarea(text) {
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch (err) {
    console.error("Error copying to clipboard", err);
    return false;
  }
}

function sortByCreatedDesc(list) {
  return [...list].sort((a, b) => {
    const ad = Date.parse(a.created_at || a.createdAt || 0) || 0;
    const bd = Date.parse(b.created_at || b.createdAt || 0) || 0;
    return bd - ad; // most recent first
  });
}

const TOPIC_SCOPE_PREVIEW_CHARS = 50;

function truncate(text, max) {
  const s = String(text || "").trim();
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max)}...` : s;
}

/**
 * A column header with a one-line note under it.
 *
 * Whoever sets up an interview type has no way to tell, from the field names
 * alone, which of them a participant will read and which ones steer the AI
 * interviewer. The hint says which — and how — so the consequence of a value
 * is visible at the point it is being chosen. Columns that are purely
 * bookkeeping (owner, counts) deliberately have no hint, so the ones that do
 * carry weight stand out.
 */
function ColumnHeader({ label, hint }) {
  return (
    // The hint needs a floor on the column width, or the table squeezes these
    // columns to the width of their cell contents and the note renders as a
    // tall one-word-per-line strip.
    <TableCell sx={{ verticalAlign: "top", minWidth: hint ? 190 : undefined }}>
      <Box sx={{ fontWeight: 600 }}>{label}</Box>
      {hint ? (
        <Box
          sx={{
            mt: 0.5,
            fontSize: "0.72rem",
            lineHeight: 1.4,
            fontWeight: 400,
            color: "text.secondary",
            whiteSpace: "normal",
          }}
        >
          {hint}
        </Box>
      ) : null}
    </TableCell>
  );
}

export function InterviewTypesAdmin() {
  const navigate = useNavigate();

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [industries, setIndustries] = useState([]);
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState({
    slug: "",
    name: "",
    script_id: "",
    interview_focus_type: "product", // e.g. "product" or "jtbd"
    jtbd_name: "",
    product_name: "",
    topic_description: "", // optional scope statement for the AI interviewer
    industry: "",
    payout_amount: "",
    estimated_minutes: "45",
    is_active: true,
    industry_ids: [],
    product_ids: [],
    owner_email: "", //  (for optional new owner user)
  });

  // company background fields (necessary for new owner)
  const [createNewOwner, setCreateNewOwner] = useState(true); // "Create new user & org" toggle (default: yes/recommended)
  const [backgroundWebsites, setBackgroundWebsites] = useState("");
  const [backgroundText, setBackgroundText] = useState("");
  const [backgroundLoading, setBackgroundLoading] = useState(false);

  // Accordion state for create form (collapsed by default)
  const [createAccordionOpen, setCreateAccordionOpen] = useState(false);

  // Create-type submission state
  const [creatingType, setCreatingType] = useState(false);
  const [createSuccess, setCreateSuccess] = useState("");

  // Inline edit state for an existing type (one row at a time)
  const [editingSlug, setEditingSlug] = useState("");
  const [editForm, setEditForm] = useState({
    name: "",
    topic: "",
    topic_description: "",
    payout_amount: "",
    estimated_minutes: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Share-link "copied" indicator (slug of the row most recently copied)
  const [copiedSlug, setCopiedSlug] = useState("");

  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(false);
  const [interviewsError, setInterviewsError] = useState("");
  const [openBySlug, setOpenBySlug] = useState({}); // { [slug]: true/false }

  // Interview Scripts
  const [scripts, setScripts] = useState([]);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [scriptsError, setScriptsError] = useState("");

  // Group interviews by interview_type_slug
  const interviewsByType = useMemo(() => {
    const map = {};

    for (const intv of interviews) {
      const slug = intv.interview_type_slug || "";
      if (!map[slug]) map[slug] = [];
      map[slug].push(intv);
    }

    return map;
  }, [interviews]);

  // Admin-only gate (client-side UX layer)
  const admin = useMemo(() => isAdmin(), []);
  useEffect(() => {
    if (!admin) {
      // Stay and show message; or hard-redirect:
      // navigate("/", { replace: true });
    }
  }, [admin, navigate]);

  async function fetchInterviews() {
    try {
      setInterviewsLoading(true);
      setInterviewsError("");
      const res = await fetch(`${BACKEND_URL}/api/interviews`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setInterviews(data);
    } catch (err) {
      console.error("Error fetching interviews", err);
      setInterviewsError("Failed to load interviews.");
    } finally {
      setInterviewsLoading(false);
    }
  }

  async function fetchTypes() {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${BACKEND_URL}/api/interview-types`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setTypes(sortByCreatedDesc(data));
    } catch (err) {
      console.error("Error fetching interview types", err);
      setError("Failed to load interview types.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchIndustries() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/industries`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setIndustries(data);
    } catch (err) {
      console.error("Error fetching industries", err);
      setError("Failed to load industries.");
    }
  }

  async function fetchProducts() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/products`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error("Error fetching products", err);
      setError("Failed to load products.");
    }
  }

  async function fetchInterviewScripts() {
    setScriptsError("");
    setScriptsLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/interview-scripts`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      // Expecting an array
      const list = Array.isArray(data) ? data : data.scripts || [];
      setScripts(list);
    } catch (err) {
      console.error("Error loading interview scripts", err);
      setScriptsError(err.message || "Failed to load interview scripts.");
    } finally {
      setScriptsLoading(false);
    }
  }

  async function handleApproveInterview(interviewId) {
    setError("");
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/interviews/${encodeURIComponent(interviewId)}/review`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            review_status: "approved",
            review_notes: "Approved from interview types page",
          }),
        }
      );

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      // Update local interviews list so UI updates immediately
      setInterviews((prev) =>
        prev.map((intv) =>
          intv.interview_id === interviewId
            ? {
                ...intv,
                review_status: body.review_status,
                review_notes: body.review_notes,
                analysis_status: body.analysis_status,
                analysis_error: body.analysis_error,
                customer_story_filename: body.customer_story_filename,
              }
            : intv
        )
      );
    } catch (err) {
      console.error("Error approving interview", err);
      setError(err.message || "Failed to approve interview.");
    }
  }
  
  useEffect(() => {
    if (admin) {
      fetchTypes();
      fetchIndustries();
      fetchProducts();
      fetchInterviews();
      fetchInterviewScripts();
    }
  }, [admin]);

  function handleFormChange(e) {
    const { name, value, type, checked, multiple, options } = e.target;

    if (name === "industry_ids" || name === "product_ids") {
      const selected = Array.from(options)
        .filter((opt) => opt.selected)
        .map((opt) => opt.value);

      setForm((prev) => ({
        ...prev,
        [name]: selected,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleGenerateBackground(e) {
    e.preventDefault();
    setError("");
    setBackgroundLoading(true);

    const token = getToken();
    if (!token) {
      setError("You must be logged in to generate company information.");
      setBackgroundLoading(false);
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ websites: backgroundWebsites }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      if (data.analysis) {
        setBackgroundText(data.analysis);
      } else {
        setError("No analysis returned from the server.");
      }
    } catch (err) {
      console.error("Error generating background info", err);
      setError(err.message || "Failed to generate background information.");
    } finally {
      setBackgroundLoading(false);
    }
  }

  async function saveBackgroundForOwner(ownerEmail, backgroundContent) {
    // This uses /api/admin/login-as to impersonate the owner for one call to /save_background,
    // without changing the admin's own access token in localStorage.
    const adminToken = getToken();
    if (!adminToken) {
      throw new Error("Missing admin token for saving background info.");
    }

    // 1) Get an impersonation token for the owner
    const loginAsRes = await fetch(`${BACKEND_URL}/api/admin/login-as`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({ email: ownerEmail }),
    });

    const loginAsData = await loginAsRes.json().catch(() => ({}));
    if (!loginAsRes.ok) {
      throw new Error(
        loginAsData.error || `login-as failed (HTTP ${loginAsRes.status})`
      );
    }

    const ownerToken = loginAsData.access_token;
    if (!ownerToken) {
      throw new Error("login-as did not return an access_token.");
    }

    // 2) Call /save_background as that owner
    const saveRes = await fetch(`${BACKEND_URL}/save_background`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${ownerToken}`,
      },
      body: JSON.stringify({ content: backgroundContent }),
    });

    const saveData = await saveRes.json().catch(() => ({}));
    if (!saveRes.ok) {
      throw new Error(
        saveData.error || "Failed to save company background for owner."
      );
    }

    return saveData;
  }

  async function handleCreateType(e) {
    e.preventDefault();
    setError("");
    setCreateSuccess("");

    const token = getToken();
    if (!token) {
      setError("You must be logged in to create interview types.");
      return;
    }

    if (!form.slug.trim()) {
      setError("Slug is required.");
      return;
    }

    if (!form.script_id.trim()) {
      setError("Interview script is required.");
      return;
    }

    const focus = (form.interview_focus_type || "product").trim().toLowerCase();
    if (focus !== "product" && focus !== "jtbd") {
      setError("Interview focus type must be product or jtbd.");
      return;
    }

    if (focus === "product") {
      if (!form.product_name.trim()) {
        setError("Product name is required for product interviews.");
        return;
      }
    } else {
      if (!form.jtbd_name.trim()) {
        setError("Job To Be Done name is required for JTBD interviews.");
        return;
      }
    }    

    // If we're creating a *new* owner user + org, require owner_email and backgroundText
    if (createNewOwner) {
      if (!form.owner_email.trim()) {
        setError("Owner email is required when creating a new user and org.");
        return;
      }
      if (!backgroundText.trim()) {
        setError(
          "Company background information is required when creating a new user and org."
        );
        return;
      }
    }

    // Build payload for /api/interview-types
    const payload = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      script_id: form.script_id.trim(),
      interview_focus_type: focus,
      jtbd_name: focus === "jtbd" ? form.jtbd_name.trim() : "",
      product_name: focus === "product" ? form.product_name.trim() : "",
      topic_description: form.topic_description.trim(),
      industry: form.industry.trim(),
      payout_amount: form.payout_amount ? parseFloat(form.payout_amount) : null,
      estimated_minutes: form.estimated_minutes ? parseInt(form.estimated_minutes, 10) : 45,
      is_active: !!form.is_active,
      industry_ids: form.industry_ids || [],
      product_ids: form.product_ids || [],
    };


    // If createNewOwner is true, we explicitly set owner_email so the backend
    // will create that user/org if needed. If false, we omit owner_email and
    // let the backend fall back to the current logged-in user as owner.
    if (createNewOwner && form.owner_email.trim()) {
      payload.owner_email = form.owner_email.trim();
    }

    setCreatingType(true);

    try {
      // 1) Create the interview type
      const res = await fetch(`${BACKEND_URL}/api/interview-types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const createdType = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(createdType.error || `HTTP ${res.status}`);
      }

      // 2) If we created a *new* owner user/org, also create their background file now
      let backgroundError = false;
      if (createNewOwner && form.owner_email.trim() && backgroundText.trim()) {
        try {
          await saveBackgroundForOwner(
            form.owner_email.trim(),
            backgroundText.trim()
          );
        } catch (bgErr) {
          backgroundError = true;
          console.error("Error saving background for owner", bgErr);
          // We don't roll back the interview type, but we surface the error so you can fix it.
          setError(
            bgErr.message ||
              "Interview type was created, but there was an error saving the company background."
          );
        }
      }

      // Clear form
      setForm({
        slug: "",
        name: "",
        script_id: "",
        interview_focus_type: "product",
        jtbd_name: "",
        product_name: "",
        topic_description: "",
        industry: "",
        payout_amount: "",
        estimated_minutes: "45",
        is_active: true,
        industry_ids: [],
        product_ids: [],
        owner_email: "",
      });

      setBackgroundWebsites("");
      setBackgroundText("");
      setCreateNewOwner(true);

      // Refresh list
      await fetchTypes();

      if (!backgroundError) {
        setCreateSuccess("Interview type created successfully.");
      }
    } catch (err) {
      console.error("Error creating interview type", err);
      setError(err.message || "Failed to create interview type.");
    } finally {
      setCreatingType(false);
    }
  }

  async function handleToggleActive(slug, currentActive) {
    setError("");

    const token = getToken();
    if (!token) {
      setError("You must be logged in to update interview types.");
      return;
    }

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/interview-types/${encodeURIComponent(slug)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({ is_active: !currentActive }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      await fetchTypes();
    } catch (err) {
      console.error("Error updating interview type", err);
      setError(err.message || "Failed to update interview type.");
    }
  }

  function startEditType(t) {
    setError("");
    const focus = t.interview_focus_type || "product";

    setEditingSlug(t.slug);
    setEditForm({
      name: t.name || "",
      topic: (focus === "jtbd" ? t.jtbd_name : t.product_name) || "",
      topic_description: t.topic_description || "",
      payout_amount: t.payout_amount != null ? String(t.payout_amount) : "",
      estimated_minutes:
        t.estimated_minutes != null ? String(t.estimated_minutes) : "",
    });
  }

  function cancelEditType() {
    setEditingSlug("");
  }

  function handleEditFormChange(e) {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSaveType(t) {
    setError("");

    const token = getToken();
    if (!token) {
      setError("You must be logged in to update interview types.");
      return;
    }

    const focus = t.interview_focus_type || "product";
    const topic = editForm.topic.trim();
    if (!topic) {
      setError(
        focus === "jtbd"
          ? "Job To Be Done name is required."
          : "Product name is required."
      );
      return;
    }

    const payout =
      editForm.payout_amount === "" ? null : parseFloat(editForm.payout_amount);
    if (payout != null && Number.isNaN(payout)) {
      setError("Payout must be a number.");
      return;
    }

    const minutes =
      editForm.estimated_minutes === ""
        ? null
        : parseInt(editForm.estimated_minutes, 10);
    if (minutes != null && Number.isNaN(minutes)) {
      setError("Estimated minutes must be a number.");
      return;
    }

    // "Topic" is a derived column: it maps to jtbd_name for JTBD interviews and
    // to product_name for product interviews (see the topic computed in render).
    const payload = {
      name: editForm.name.trim(),
      payout_amount: payout,
      estimated_minutes: minutes,
      topic_description: editForm.topic_description.trim(),
      ...(focus === "jtbd" ? { jtbd_name: topic } : { product_name: topic }),
    };

    setSavingEdit(true);

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/interview-types/${encodeURIComponent(t.slug)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      setEditingSlug("");
      await fetchTypes();
    } catch (err) {
      console.error("Error updating interview type", err);
      setError(err.message || "Failed to update interview type.");
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleCopyShareLink(slug, shareUrl) {
    let copied = false;

    // navigator.clipboard is unavailable outside secure contexts and can be
    // permission-denied, so fall back to the legacy selection-based copy.
    try {
      await navigator.clipboard.writeText(shareUrl);
      copied = true;
    } catch (err) {
      copied = copyViaTextarea(shareUrl);
    }

    if (!copied) {
      setError("Could not copy the link to your clipboard.");
      return;
    }

    setCopiedSlug(slug);
    setTimeout(
      () => setCopiedSlug((prev) => (prev === slug ? "" : prev)),
      2000
    );
  }

  async function handleLoginAsOwner(ownerEmail) {
    setError("");

    if (!ownerEmail) {
      setError("This interview type does not have an owner_email set.");
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login-as`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ email: ownerEmail }),
      });

      // Parse JSON exactly once
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const token = data.access_token;
      if (!token) {
        throw new Error("No access_token returned from login-as.");
      }

      // IMPORTANT: store the new JWT where your app expects it.
      localStorage.setItem("access_token", token);

      // Optional: store user email if your app cares
      // localStorage.setItem("user_email", ownerEmail);

      // Reload as the impersonated user
      window.location.href = "/";
    } catch (err) {
      console.error("Error in Log in as owner", err);
      setError(err.message || "Failed to log in as owner.");
    }
  }

  const scriptLabelById = useMemo(() => {
    const map = {};
    for (const s of scripts || []) {
      const id = s.script_id || s.id || s.filename || "";
      if (!id) continue;
      const name = s.name || id;
      const desc = s.description ? ` — ${s.description}` : "";
      map[id] = `${name}${desc}`;
    }
    return map;
  }, [scripts]);


  // ---------- RENDER ----------
  return (
    <ThemeProvider theme={theme}>
      <NavBar />

      <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Interview Types (Admin)
        </Typography>

        {!admin ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Admins only. You don’t have access to this page.
            </Alert>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Go to Home
            </Button>
          </Paper>
        ) : (
          <>
            {/* Global error message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Create new interview type (Accordion) */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Accordion
                expanded={createAccordionOpen}
                onChange={(_, expanded) => setCreateAccordionOpen(expanded)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Create new interview type
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {createSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {createSuccess}
                    </Alert>
                  )}

                  <form onSubmit={handleCreateType}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      }}
                    >
                      <label>
                        Slug* (URL-friendly, e.g.{" "}
                        <code>airdna-short-term-rental</code>):
                        <input
                          type="text"
                          name="slug"
                          value={form.slug}
                          onChange={handleFormChange}
                          required
                          style={{ width: "100%", marginTop: 4 }}
                        />
                      </label>

                      <label>
                        Name (display name):
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleFormChange}
                          style={{ width: "100%", marginTop: 4 }}
                        />
                      </label>

                      <label style={{ gridColumn: "1 / -1" }}>
                        Interview focus*:
                        <div style={{ marginTop: 6 }}>
                          <label style={{ marginRight: 16 }}>
                            <input
                              type="radio"
                              name="interview_focus_type"
                              value="product"
                              checked={(form.interview_focus_type || "product") === "product"}
                              onChange={handleFormChange}
                              style={{ marginRight: 6 }}
                            />
                            Product-specific deep dive
                          </label>

                          <label>
                            <input
                              type="radio"
                              name="interview_focus_type"
                              value="jtbd"
                              checked={form.interview_focus_type === "jtbd"}
                              onChange={handleFormChange}
                              style={{ marginRight: 6 }}
                            />
                            Job To Be Done deep dive (explores whatever solution they use to get the job done)
                          </label>
                        </div>
                      </label>

                      {(form.interview_focus_type || "product") === "product" ? (
                        <label>
                          Product name*:
                          <input
                            type="text"
                            name="product_name"
                            value={form.product_name}
                            onChange={handleFormChange}
                            required
                            style={{ width: "100%", marginTop: 4 }}
                          />
                        </label>
                      ) : (
                        <label>
                          Job To Be Done name*:
                          <input
                            type="text"
                            name="jtbd_name"
                            value={form.jtbd_name}
                            onChange={handleFormChange}
                            required
                            placeholder='e.g. "Reconciling monthly expenses"'
                            style={{ width: "100%", marginTop: 4 }}
                          />
                        </label>
                      )}

                      <label>
                        Topic scope (optional — guides the AI interviewer):
                        <textarea
                          name="topic_description"
                          value={form.topic_description}
                          onChange={handleFormChange}
                          rows={3}
                          placeholder={
                            (form.interview_focus_type || "product") === "jtbd"
                              ? 'What this job includes and what it does NOT, e.g. "Setting nightly rates for a listing: researching comps, seasonal/event adjustments, pricing tools. Not in scope: guest issues, cleaning, maintenance, bookings."'
                              : 'What using this product covers and what it does NOT, so the interviewer stays on topic.'
                          }
                          style={{ width: "100%", marginTop: 4, fontFamily: "inherit" }}
                        />
                      </label>

                      <label>
                        Interview script*:
                        <select
                          name="script_id"
                          value={form.script_id}
                          onChange={handleFormChange}
                          required
                          style={{ width: "100%", marginTop: 4 }}
                        >
                          <option value="">
                            {scriptsLoading ? "Loading scripts…" : "Select a script…"}
                          </option>
                          {(scripts || []).map((s) => {
                            const id = s.script_id || s.id || s.filename || "";
                            const name = s.name || id;
                            const desc = s.description ? ` — ${s.description}` : "";
                            return (
                              <option key={id} value={id}>
                                {name}{desc}
                              </option>
                            );
                          })}
                        </select>

                        {scriptsError ? (
                          <div style={{ color: "crimson", fontSize: 12, marginTop: 4 }}>
                            {scriptsError}
                          </div>
                        ) : null}
                      </label>

                      <label>
                        Industry:
                        <input
                          type="text"
                          name="industry"
                          value={form.industry}
                          onChange={handleFormChange}
                          style={{ width: "100%", marginTop: 4 }}
                        />
                      </label>

                      <label>
                        Industries (for subscription routing):
                        <select
                          multiple
                          name="industry_ids"
                          value={form.industry_ids}
                          onChange={handleFormChange}
                          style={{
                            width: "100%",
                            marginTop: 4,
                            minHeight: 80,
                          }}
                        >
                          {industries.map((ind) => (
                            <option key={ind.id} value={ind.id}>
                              {ind.name} ({ind.slug})
                            </option>
                          ))}
                        </select>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Hold Ctrl (Windows) or Command (Mac) to select
                          multiple.
                        </div>
                      </label>

                      <label>
                        Products (for subscription routing):
                        <select
                          multiple
                          name="product_ids"
                          value={form.product_ids}
                          onChange={handleFormChange}
                          style={{
                            width: "100%",
                            marginTop: 4,
                            minHeight: 80,
                          }}
                        >
                          {products.map((prod) => (
                            <option key={prod.id} value={prod.id}>
                              {prod.name} ({prod.slug})
                            </option>
                          ))}
                        </select>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Link each interview type to one or more products.
                        </div>
                      </label>

                      <label>
                        Payout amount (USD):
                        <input
                          type="number"
                          name="payout_amount"
                          value={form.payout_amount}
                          onChange={handleFormChange}
                          min="0"
                          step="1"
                          style={{ width: "100%", marginTop: 4 }}
                        />
                      </label>

                      <label>
                        Estimated minutes:
                        <input
                          type="number"
                          name="estimated_minutes"
                          value={form.estimated_minutes}
                          onChange={handleFormChange}
                          min="1"
                          step="1"
                          style={{ width: "100%", marginTop: 4 }}
                        />
                      </label>

                      <label style={{ gridColumn: "1 / -1" }}>
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={form.is_active}
                          onChange={handleFormChange}
                          style={{ marginRight: 6 }}
                        />
                        Active (allow new participants)
                      </label>

                      {/* NEW: Create new owner toggle */}
                      <label style={{ gridColumn: "1 / -1" }}>
                        Create new user &amp; org to own this interview type?
                        (recommended)
                        <br />
                        <label style={{ marginRight: 12 }}>
                          <input
                            type="radio"
                            name="createNewOwner"
                            value="yes"
                            checked={createNewOwner === true}
                            onChange={() => setCreateNewOwner(true)}
                            style={{ marginRight: 4, marginTop: 8 }}
                          />
                          Yes – create a new owner user &amp; org for this
                          product.
                        </label>
                        <br />
                        <label>
                          <input
                            type="radio"
                            name="createNewOwner"
                            value="no"
                            checked={createNewOwner === false}
                            onChange={() => setCreateNewOwner(false)}
                            style={{ marginRight: 4, marginTop: 4 }}
                          />
                          No – use my current account (or existing owner)
                          instead.
                        </label>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          When you choose "Yes", the email and company
                          background below are required.
                        </div>
                      </label>

                      {/* NEW: Owner + company background fields (shown when creating a new owner) */}
                      {createNewOwner && (
                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                            mt: 1,
                            p: 2,
                            border: "1px solid #ddd",
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Owner user &amp; company background for AI analysis
                          </Typography>

                          <label
                            style={{ display: "block", marginBottom: 8 }}
                          >
                            Type owner user (email address)*:
                            <input
                              type="email"
                              name="owner_email"
                              value={form.owner_email}
                              onChange={handleFormChange}
                              placeholder="owner@example.com"
                              style={{ width: "100%", marginTop: 4 }}
                            />
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              We’ll create a new user and organization for this
                              email if it doesn’t exist yet. The default
                              password will be <code>Password123!</code>.
                            </div>
                          </label>

                          <label
                            style={{ display: "block", marginBottom: 8 }}
                          >
                            Company website(s) (optional, for auto-generating
                            background):
                            <textarea
                              rows={2}
                              value={backgroundWebsites}
                              onChange={(e) =>
                                setBackgroundWebsites(e.target.value)
                              }
                              placeholder="Provide link(s) to your company or product website(s)"
                              style={{ width: "100%", marginTop: 4 }}
                            />
                          </label>

                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleGenerateBackground}
                            disabled={backgroundLoading}
                            sx={{ mb: 2 }}
                          >
                            {backgroundLoading
                              ? "Generating…"
                              : "Generate company information"}
                          </Button>

                          <label style={{ display: "block" }}>
                            Company background for AI*:
                            <textarea
                              rows={4}
                              value={backgroundText}
                              onChange={(e) =>
                                setBackgroundText(e.target.value)
                              }
                              placeholder="Describe your company and product so the AI can interpret interviews correctly."
                              style={{ width: "100%", marginTop: 4 }}
                            />
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              This text will be saved as the background
                              information file for this new owner organization.
                            </div>
                          </label>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={creatingType}
                      >
                        {creatingType ? (
                          <>
                            <CircularProgress size={18} sx={{ mr: 1 }} />
                            Creating…
                          </>
                        ) : (
                          "Create type"
                        )}
                      </Button>
                    </Box>
                  </form>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* Existing types table */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Existing interview types
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={22} />
                  <Typography>Loading…</Typography>
                </Box>
              ) : types.length === 0 ? (
                <Typography>No interview types defined yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell /> {/* Expand/collapse arrow column */}
                        <ColumnHeader
                          label="Slug"
                          hint="Participants: it's the share link they open (/participate/slug)."
                        />
                        <ColumnHeader
                          label="Name"
                          hint="Internal label. Participants only see it if Topic is empty."
                        />
                        <ColumnHeader
                          label="Focus"
                          hint="Interviewer: frames the interview as a job vs. a product, and picks the wording on the landing page (“how you handle…” vs “how you use…”)."
                        />
                        <ColumnHeader
                          label="Topic"
                          hint="Participants: headline of the landing page. Interviewer: says it by name in place of {{topic}} in the script, so questions never say a vague “this”."
                        />
                        <ColumnHeader
                          label="Topic scope"
                          hint="Interviewer only, never shown to participants: what the topic includes and excludes. Keeps follow-ups on the topic and stops the AI chasing an unrelated tangent."
                        />
                        <ColumnHeader
                          label="Script"
                          hint="Interviewer: the set of questions it works through, and the follow-up budget for each."
                        />
                        <ColumnHeader
                          label="Industry"
                          hint="Not shown to participants. Recorded on the interview and given as context when the story is summarized."
                        />
                        <TableCell>Owner</TableCell>
                        <TableCell>Owner Org</TableCell>
                        <ColumnHeader
                          label="Payout"
                          hint="Participants: shown as the payout on the landing page and the ad."
                        />
                        <ColumnHeader
                          label="Est. minutes"
                          hint="Participants: shown as the expected length before they commit."
                        />
                        <TableCell>Interviews submitted</TableCell>
                        <TableCell>Interviews approved</TableCell>
                        <ColumnHeader
                          label="Active?"
                          hint="Participants: when No, the share link turns them away instead of starting."
                        />
                        <ColumnHeader
                          label="Share link"
                          hint="Participants: the link to give them. Opens the landing page for this type."
                        />
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {types.map((t) => {
                        const baseUrl = window.location.origin;
                        const shareUrl = `${baseUrl}/participate/${t.slug}`;

                        const focus = t.interview_focus_type || "product";
                        const topic =
                          focus === "jtbd"
                            ? (t.jtbd_name || t.product_name || "-")
                            : (t.product_name || "-");

                        const scriptId = t.script_id || "";
                        const scriptLabel = scriptLabelById[scriptId] || scriptId || "-";

                        const isOpen = !!openBySlug[t.slug];
                        const typeInterviews = interviewsByType[t.slug] || [];
                        const isEditing = editingSlug === t.slug;

                        return (
                          <React.Fragment key={t.id || t.slug}>
                            {/* Row 1: the normal interview type row */}
                            <TableRow>
                              <TableCell width={48}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setOpenBySlug((prev) => ({ ...prev, [t.slug]: !prev[t.slug] }))
                                  }
                                  aria-label={isOpen ? "collapse" : "expand"}
                                >
                                  {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              </TableCell>

                              <TableCell>{t.slug}</TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    name="name"
                                    label="Name"
                                    value={editForm.name}
                                    onChange={handleEditFormChange}
                                    disabled={savingEdit}
                                    sx={{ minWidth: 160 }}
                                  />
                                ) : (
                                  t.name
                                )}
                              </TableCell>
                              <TableCell>{focus}</TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    name="topic"
                                    label={
                                      focus === "jtbd"
                                        ? "Job To Be Done"
                                        : "Product name"
                                    }
                                    value={editForm.topic}
                                    onChange={handleEditFormChange}
                                    disabled={savingEdit}
                                    sx={{ minWidth: 160 }}
                                  />
                                ) : (
                                  topic
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    name="topic_description"
                                    label="Topic scope (optional)"
                                    value={editForm.topic_description}
                                    onChange={handleEditFormChange}
                                    disabled={savingEdit}
                                    multiline
                                    minRows={3}
                                    maxRows={10}
                                    sx={{ minWidth: 280 }}
                                  />
                                ) : t.topic_description ? (
                                  // Truncated: these run to a paragraph. The full
                                  // text is in the tooltip and in the edit field.
                                  <Tooltip title={t.topic_description}>
                                    <Box
                                      sx={{
                                        fontSize: "0.8rem",
                                        color: "text.secondary",
                                        maxWidth: 260,
                                        cursor: "help",
                                      }}
                                    >
                                      {truncate(
                                        t.topic_description,
                                        TOPIC_SCOPE_PREVIEW_CHARS
                                      )}
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}>
                                  {scriptLabel}
                                </Box>
                              </TableCell>
                              <TableCell>{t.industry}</TableCell>
                              <TableCell>{t.owner_email || "-"}</TableCell>
                              <TableCell>{t.organization_id || "-"}</TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    name="payout_amount"
                                    label="Payout (USD)"
                                    value={editForm.payout_amount}
                                    onChange={handleEditFormChange}
                                    disabled={savingEdit}
                                    inputProps={{ min: 0, step: 1 }}
                                    sx={{ width: 120 }}
                                  />
                                ) : t.payout_amount != null ? (
                                  `$${t.payout_amount}`
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    name="estimated_minutes"
                                    label="Minutes"
                                    value={editForm.estimated_minutes}
                                    onChange={handleEditFormChange}
                                    disabled={savingEdit}
                                    inputProps={{ min: 1, step: 1 }}
                                    sx={{ width: 110 }}
                                  />
                                ) : (
                                  t.estimated_minutes || "-"
                                )}
                              </TableCell>
                              <TableCell>{t.interviews_submitted_count ?? 0}</TableCell>
                              <TableCell>{t.interviews_approved_count ?? 0}</TableCell>
                              <TableCell>{t.is_active ? "Yes" : "No"}</TableCell>

                              <TableCell>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    href={shareUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ whiteSpace: "nowrap" }}
                                  >
                                    Open link
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ whiteSpace: "nowrap" }}
                                    color={copiedSlug === t.slug ? "success" : "primary"}
                                    startIcon={
                                      copiedSlug === t.slug ? (
                                        <CheckIcon fontSize="small" />
                                      ) : (
                                        <ContentCopyIcon fontSize="small" />
                                      )
                                    }
                                    onClick={() => handleCopyShareLink(t.slug, shareUrl)}
                                  >
                                    {copiedSlug === t.slug ? "Copied!" : "Copy link"}
                                  </Button>
                                </Box>
                              </TableCell>

                              <TableCell>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                  {isEditing ? (
                                    <>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleSaveType(t)}
                                        disabled={savingEdit}
                                      >
                                        {savingEdit ? "Saving…" : "Save"}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={cancelEditType}
                                        disabled={savingEdit}
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => startEditType(t)}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleToggleActive(t.slug, !!t.is_active)}
                                    disabled={isEditing}
                                  >
                                    {t.is_active ? "Deactivate" : "Activate"}
                                  </Button>
                                  {t.owner_email && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleLoginAsOwner(t.owner_email)}
                                      disabled={isEditing}
                                    >
                                      Log in as owner
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>

                            {/* Row 2: the expandable area */}
                            <TableRow>
                              <TableCell colSpan={17} sx={{ p: 0 }}>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2 }}>
                                    {interviewsError ? (
                                      <Alert severity="error">{interviewsError}</Alert>
                                    ) : interviewsLoading ? (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CircularProgress size={18} />
                                        <Typography>Loading interviews…</Typography>
                                      </Box>
                                    ) : (
                                      <AdminInterviewsTable
                                        interviews={typeInterviews}
                                        onApprove={handleApproveInterview}
                                      />
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default InterviewTypesAdmin;
